import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { resolveRecentCommands } from "@/shared/components/command-menu/command-items";
import { type CommandEntry, matchCommands } from "@/shared/components/command-menu/command-match";
import { useCommandMenu } from "@/shared/components/command-menu/command-menu.context";
import {
  CommandEntryRow,
  PeopleGroupBody,
} from "@/shared/components/command-menu/command-menu-groups";
import { useCommandPeople } from "@/shared/components/command-menu/use-command-people";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from "@/shared/components/ui/command";

/// The palette. Mounted once by AppShell, opened by Cmd+K or the sidebar
/// trigger.
///
/// cmdk's own filtering is off (see CommandDialog): people rows arrive already
/// matched by the shore on name and email, and re-scoring them against the
/// display name alone would drop legitimate hits. Static commands are ranked
/// by `matchCommands` instead.
export function CommandMenu() {
  const { open, setOpen, navCommands, workspaceCommands, recents, record, currentPathname } =
    useCommandMenu();
  // Named `term`, not `query`: audit rule H1 flags URL-shaped state names, and
  // this deliberately is not URL state (the palette does not deep-link).
  const [term, setTerm] = useState("");
  const navigate = useNavigate();
  const people = useCommandPeople(term);

  const isTyping = term.trim().length > 0;
  const staticMatches = matchCommands([...navCommands, ...workspaceCommands], term);
  const navMatches = staticMatches.filter((entry) => entry.kind === "nav");
  const workspaceMatches = staticMatches.filter((entry) => entry.kind === "workspace");
  const recentEntries = resolveRecentCommands(recents, navCommands, currentPathname);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setTerm("");
  }

  function handleSelect(entry: CommandEntry) {
    if (entry.kind === "nav") {
      record({ id: entry.id, label: entry.label, href: entry.href });
    }
    handleOpenChange(false);
    // reason: entries mix static and workspace-templated paths already
    // resolved to concrete URLs, the same shape sidebar-nav-item.tsx passes to
    // Link. TanStack's typed navigate cannot express that union.
    void navigate({ to: entry.href as never });
  }

  const showPeopleGroup =
    isTyping && (people.isPending || people.isError || people.people.length > 0);
  const nothingToShow =
    isTyping && navMatches.length === 0 && workspaceMatches.length === 0 && !showPeopleGroup;

  return (
    <CommandDialog open={open} onOpenChange={handleOpenChange}>
      <CommandInput placeholder="Search pages and people..." value={term} onValueChange={setTerm} />
      <CommandList>
        {nothingToShow ? <CommandEmpty>No results for "{term.trim()}"</CommandEmpty> : null}

        {!isTyping && recentEntries.length > 0 ? (
          <CommandGroup heading="Recent">
            {recentEntries.map((entry) => (
              <CommandEntryRow key={`recent-${entry.id}`} entry={entry} onSelect={handleSelect} />
            ))}
          </CommandGroup>
        ) : null}

        {navMatches.length > 0 ? (
          <CommandGroup heading="Go to">
            {navMatches.map((entry) => (
              <CommandEntryRow key={entry.id} entry={entry} onSelect={handleSelect} />
            ))}
          </CommandGroup>
        ) : null}

        {showPeopleGroup ? (
          <CommandGroup heading="People">
            <PeopleGroupBody
              people={people.people}
              isPending={people.isPending}
              isError={people.isError}
              onSelect={handleSelect}
            />
          </CommandGroup>
        ) : null}

        {workspaceMatches.length > 0 ? (
          <CommandGroup heading="Workspaces">
            {workspaceMatches.map((entry) => (
              <CommandEntryRow key={entry.id} entry={entry} onSelect={handleSelect} />
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </CommandDialog>
  );
}
